import React, { useState } from "react";
import {
  Table, Button, Space, Tag, message, Popconfirm,
  Form, Input, Drawer, Image, Upload, InputNumber, Select
} from 'antd'
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useApiSelector } from 'src/hooks'
import { useRequest, dispatchAsync } from 'friday-async'
import jsCookie from 'js-cookie'
import config from 'src/config'

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

const BottomMenu = () => {
  const apis = useApiSelector();
  const { dataArray, isValidating, revalidate } = useRequest(apis.bottomMenu.list(), {
    paginated: false,
  })

  const [drawer, setDrawer] = useState<{ visible: boolean; info: any }>({
    visible: false, info: null
  })
  const [uploading, setUploading] = useState(false)
  const [form] = Form.useForm()
  const isEdit = !!drawer.info

  // ====== CRUD ======
  const handleCreate = () => setDrawer({ visible: true, info: null })

  const handleEdit = (record: any) => {
    setDrawer({ visible: true, info: record })
  }

  const handleSubmit = async () => {
    try {
      const vals = await form.validateFields()
      let error: any
      if (isEdit) {
        const r = await dispatchAsync(apis.bottomMenu.update({ _id: drawer.info._id, ...vals }))
        error = r.error
      } else {
        const r = await dispatchAsync(apis.bottomMenu.create(vals))
        error = r.error
      }
      if (error) return message.error(isEdit ? '编辑失败' : '创建失败')
      message.success(isEdit ? '编辑成功' : '创建成功')
      setDrawer({ visible: false, info: null })
      form.resetFields()
      revalidate()
    } catch (err) { console.log(err) }
  }

  const handleRemove = async (_id: string) => {
    const { error } = await dispatchAsync(apis.bottomMenu.remove({ _id }))
    if (error) return message.error('删除失败')
    message.success('删除成功')
    revalidate()
  }

  // ====== 图标上传 ======
  const handleUpload = (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true)
      return
    }
    if (info.file.status === 'done') {
      setUploading(false)
      const res = info.file.response
      if (res?.data?.url) {
        form.setFieldsValue({ icon: res.data.url })
        message.success('图标上传成功')
      }
    } else if (info.file.status === 'error') {
      setUploading(false)
      message.error('图标上传失败')
    }
  }

  const iconUrl = Form.useWatch('icon', form)

  // ====== 表格列 ======
  const columns = [
    { title: '标题', dataIndex: 'title', key: 'title', width: 150 },
    {
      title: '图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
      align: 'center' as const,
      render: (v: string) => v ? (
        <Image
          src={`${config.imgHost}${v.startsWith('/') ? v.slice(1) : v}`}
          alt="图标"
          width={40}
          height={40}
          style={{ objectFit: 'contain', borderRadius: 4 }}
          preview={{ mask: '预览' }}
        />
      ) : <Tag>-</Tag>,
    },
    {
      title: '跳转链接',
      dataIndex: 'linkUrl',
      key: 'linkUrl',
      width: 250,
      ellipsis: true,
      render: (v: string) => v || <Tag color="orange">未设置</Tag>,
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
      align: 'center' as const,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: string) => v === 'enabled'
        ? <Tag color="green">启用</Tag>
        : <Tag color="default">禁用</Tag>,
    },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 180 },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除该菜单？" onConfirm={() => handleRemove(record._id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Button type="primary" ghost icon={<PlusOutlined />} onClick={handleCreate}>
        新建菜单
      </Button>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={dataArray}
        loading={isValidating}
        pagination={false}
      />

      {/* 新增/编辑 抽屉 */}
      <Drawer
        title={isEdit ? '编辑菜单' : '新建菜单'}
        width={680}
        open={drawer.visible}
        onClose={() => {
          setDrawer({ visible: false, info: null })
          form.resetFields()
        }}
        destroyOnClose
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button style={{ width: 120 }} onClick={() => {
              setDrawer({ visible: false, info: null })
              form.resetFields()
            }}>取消</Button>
            <Button style={{ width: 120 }} type="primary" onClick={handleSubmit}>确认</Button>
          </Space>
        }
      >
        <Form {...layout} form={form}
          initialValues={isEdit ? drawer.info : { sortOrder: 0, status: 'enabled' }}
        >
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入菜单标题' }]}>
            <Input placeholder="如 首页" />
          </Form.Item>
          <Form.Item name="icon" label="图标">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Upload
                  name="image"
                  showUploadList={false}
                  headers={{
                    Authorization: `Bearer ${jsCookie.get('token_jwt') || ''}`
                  }}
                  action={`${config.host}/api/platform/activity/uploadImage`}
                  onChange={handleUpload}
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>上传</Button>
                </Upload>
              </Space>
              {iconUrl && (
                <Image
                  src={`${config.imgHost}${iconUrl.startsWith('/') ? iconUrl.slice(1) : iconUrl}`}
                  alt="预览"
                  style={{ maxWidth: 80, maxHeight: 80, objectFit: 'contain', borderRadius: 4, border: '1px solid #d9d9d9' }}
                  preview={{ mask: '预览' }}
                />
              )}
            </Space>
          </Form.Item>
          <Form.Item name="linkUrl" label="跳转链接" rules={[{ required: true, message: '请输入跳转链接' }]}>
            <Input placeholder="如 /pages/activity/detail/detail?code=xxx" />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序权重">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="数字越小越靠前" />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select>
              <Select.Option value="enabled">启用</Select.Option>
              <Select.Option value="disabled">禁用</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Drawer>
    </Space>
  )
}

export default BottomMenu
